import { useEntitiesServiceEntitiesPersonsListKey } from '@insurance/api/queries'
import { EntitiesService, UsersService } from '@insurance/api/requests'
import { CheckboxCardGroup, RadioCardGroup } from '@insurance/components'
import { Text } from '@shared/components'
import { useDebounce, useIntersectionObserver } from '@shared/hooks'
import { getFullName } from '@shared/utils'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Flex, Input, Modal, ModalProps, Spin } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'
import { CommonType, PersonType, PersonValueMap } from './types'

type Props<M extends boolean, P extends PersonType = PersonType> = {
  onCancel?: () => void
} & CommonType<M, P> &
  ModalProps

const LARGE_PAGE_SIZE = 50

export function PersonModalSelect<
  M extends boolean,
  P extends PersonType = PersonType
>({
  personType,
  multiple,
  value: externValue,
  onAdd,
  getTitle,
  getSubtitle,
  getExtra,
  ...props
}: Props<M, P>) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { styles } = useStyles()
  const [value, setValue] = useState<CommonType<M, P>['value']>(externValue)

  const debouncedSearch = useDebounce(search)

  useEffect(() => {
    setValue(externValue)
  }, [externValue])

  const title = useMemo(() => {
    const titleDict: Record<
      Required<CommonType>['personType'],
      React.ReactNode
    > = {
      person: t('individual_persons'),
      legalentity: t('legal_persons'),
      client: t('clients')
    }

    return titleDict[personType]
  }, [t, personType])

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useEntitiesServiceEntitiesPersonsListKey,
      debouncedSearch,
      personType
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      let response: {
        count: number
        next?: string | null
        previous?: string | null
        results?: CommonType<true, PersonType>['value']
      } = {
        count: 0,
        results: []
      }

      if (personType === 'person')
        response = await EntitiesService.entitiesPersonsList(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          pageParam,
          LARGE_PAGE_SIZE,
          debouncedSearch
        )

      if (personType === 'legalentity')
        response = await EntitiesService.entitiesLegalEntitiesList(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          pageParam,
          LARGE_PAGE_SIZE,
          debouncedSearch
        )

      if (personType === 'client')
        response = await UsersService.usersCustomersList(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          pageParam,
          LARGE_PAGE_SIZE,
          undefined,
          undefined,
          undefined,
          undefined,
          debouncedSearch
        )

      return {
        data: response?.results,
        total_pages: Math.ceil((response?.count || 0) / LARGE_PAGE_SIZE),
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: CommonType<true, PersonType>['value']
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!props.open
  })

  const { observerElement } = useIntersectionObserver({
    onIntersect: fetchNextPage,
    enabled: !!props.open
  })

  const persons = data?.pages.flatMap((page) => page.data)

  const handleOk = () => {
    onAdd?.(value)

    props.onCancel?.()
  }

  const checkboxValues = value as CommonType<true, PersonType>['value']
  const setCheckboxValues = setValue as React.Dispatch<
    React.SetStateAction<CommonType<true, PersonType>['value']>
  >

  const radioValue = value as CommonType<false, PersonType>['value']
  const setRadioValue = setValue as React.Dispatch<
    React.SetStateAction<CommonType<false, PersonType>['value']>
  >

  const componentTitle = (person: CommonType<false, PersonType>['value']) => {
    if (!person) return null

    if (getTitle) return getTitle(person as PersonValueMap[P])

    if ('name' in person) return <Text strong>{person.name}</Text>

    if ('first_name' in person) return <Text strong>{getFullName(person)}</Text>

    return null
  }

  const componentSubtitle = (
    person: CommonType<false, PersonType>['value']
  ) => {
    if (!person) return null

    if (getSubtitle) return getSubtitle(person as PersonValueMap[P])

    if ('identification_number' in person)
      return (
        <Text strong>
          {t('idnp')}: {person?.identification_number}
        </Text>
      )

    return null
  }

  const componentExtra = (person: CommonType<false, PersonType>['value']) => {
    if (!person) return null

    if (getExtra) return getExtra(person as PersonValueMap[P])

    return null
  }

  return (
    <Modal title={title} onOk={handleOk} {...props}>
      <Flex vertical gap={12}>
        <Input
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Flex vertical gap={12} className={styles.content}>
          {multiple ? (
            <CheckboxCardGroup
              value={checkboxValues?.map(({ id }) => id)}
              options={persons?.map((person) => ({
                label: (
                  <Flex vertical>
                    {componentTitle(person)}
                    {componentSubtitle(person)}
                    {componentExtra(person)}
                  </Flex>
                ),
                value: person?.id,
                colProps: { xs: 24, lg: 12 },
                onChange: () =>
                  setCheckboxValues((values) => [
                    ...(values?.filter(({ id }) => id !== person?.id) || []),
                    ...(person && !values?.some(({ id }) => id === person?.id)
                      ? [person]
                      : [])
                  ])
              }))}
            />
          ) : (
            <RadioCardGroup
              value={radioValue?.id}
              options={persons?.map((person) => ({
                label: (
                  <Flex vertical>
                    {componentTitle(person)}
                    {componentSubtitle(person)}
                    {componentExtra(person)}
                  </Flex>
                ),
                value: person?.id,
                colProps: { xs: 24, lg: 12 },
                onChange: () => setRadioValue(person)
              }))}
            />
          )}

          {isFetching && (
            <Flex justify="center">
              <Spin spinning />
            </Flex>
          )}

          {observerElement}
        </Flex>
      </Flex>
    </Modal>
  )
}
